import { Box } from '@mui/material';

export const VAR_REVIEW_LABEL = 'VAR — REVIEW IN PROGRESS';

interface VarReviewBannerProps {
  /**
   * The backend-authored `MatchState.lastVarActive`. Passed in rather than
   * gating the whole component at the call site so the live region below stays
   * MOUNTED — see the note on it.
   */
  active: boolean;
  /**
   * Compact variant for rows built out of 20px pills (the soccer `ScoreLine`),
   * where the full-size banner dominates and misaligns the row.
   */
  dense?: boolean;
}

/**
 * A prominent VAR banner. Purely a RENDER of the backend-authored
 * `MatchState.lastVarActive` flag (VAR incident Id 1074) — the FE derives
 * nothing from the raw incident stream.
 *
 * Shared by SniperEventCard and LiveMatchEventsTab's soccer ScoreLine so the two
 * surfaces cannot drift.
 *
 * The pulse animates `opacity` only — deliberately NOT `transform: scale`, which
 * would grow the banner past its host row's 8px gap and collide with the
 * adjacent chips at every peak. The trough is kept at 0.7 with pure black text:
 * the banner composites over the dark Paper while pulsing, and a lower trough (or
 * the theme's `warning.contrastText`, which is only `rgba(0,0,0,0.87)`) drops the
 * effective contrast below the WCAG AA 4.5:1 floor for half of every cycle.
 */
export function VarReviewBanner({ active, dense = false }: VarReviewBannerProps) {
  return (
    <>
      {/* A live region inserted TOGETHER with its content is generally not
          announced — the region has to already exist and only its text change.
          So this stays mounted and toggles text; it is visually hidden and
          out of flow, so it adds no gap to the host flex row. The visible
          banner is aria-hidden to avoid announcing the same thing twice. */}
      <Box
        component="span"
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          // '1px', not 1: MUI's sizing system reads a numeric <= 1 as a
          // PERCENTAGE, so `width: 1` would be `100%` of the containing block —
          // an out-of-flow 100%-wide box that can push a horizontal scrollbar.
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap'
        }}
      >
        {active ? VAR_REVIEW_LABEL : ''}
      </Box>
      {active && (
        <Box
          aria-hidden="true"
          data-testid="var-review-banner"
          // Surfaced so a host surface's test can pin WHICH variant it uses —
          // otherwise dropping `dense` at a call site is invisible to every test.
          data-dense={dense ? 'true' : 'false'}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            gap: dense ? 0.5 : 0.75,
            px: dense ? 0.75 : 1.5,
            py: dense ? 0 : 0.5,
            height: dense ? 20 : undefined,
            borderRadius: 1,
            bgcolor: 'warning.main',
            color: 'common.black',
            fontWeight: dense ? 700 : 800,
            fontSize: dense ? '0.7rem' : '0.8rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
            boxShadow: dense ? 0 : 3,
            border: dense ? '1px solid' : '2px solid',
            borderColor: 'warning.dark',
            animation: 'var-review-pulse 1.2s ease-in-out infinite',
            '@keyframes var-review-pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 }
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none'
            }
          }}
        >
          <span aria-hidden="true">📺 🚨</span>
          {VAR_REVIEW_LABEL}
        </Box>
      )}
    </>
  );
}
